import { useState } from "react";

import { getIconNameByFileExtension, Icon } from "components/icon/Icon";
import { Button } from "components/button/Button";
import { UPLOAD_FILES_LIMIT } from "./fileInputConsts";
import UploadConfirmationModal from "./UploadConfirmationModal";
import { FileInput } from "./FileInput";

type FileUploadListProps = {
  onUploadClick: (files: File[]) => void;
  filesNumberLimit?: number;
  fileSizeLimit?: number;
  isUploading: boolean;
};

export function FileUploadList({
  onUploadClick,
  filesNumberLimit = UPLOAD_FILES_LIMIT,
  fileSizeLimit,
  isUploading,
  ...props
}: FileUploadListProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const formatFileSize = (fileSize: number) => {
    if (fileSize < 1000000) {
      return Math.floor(fileSize / 1000) + " kB";
    }
    return Math.floor(fileSize / 1000000) + " MB";
  };

  const onItemRemove = (file: File) => {
    const filesRemaining = files.filter((f) => f.name !== file.name);
    setFiles(filesRemaining);
  };

  const onFilesAdded = (addedFiles: File[]) => {
    setFiles([...addedFiles, ...files]);
  };

  const renderFilesTable = (isInModal = false) => (
    <table className="file-upload-list pt-4">
      <thead>
        <tr>
          <th>Namn</th>
          {!isInModal && (
            <>
              <th>Storlek</th>
              <th></th>
            </>
          )}
        </tr>
      </thead>
      <tbody>
        {files.map((item) => {
          const iconName = getIconNameByFileExtension(item.name);

          return (
            <tr key={`${item.name}-${item.size}`}>
              <td data-testid={item.name}>
                <Icon name={iconName} className="mr-2" /> {item.name}
              </td>
              {!isInModal && (
                <>
                  <td>{formatFileSize(item.size)}</td>
                  <td>
                    <Icon
                      data-testid="file-input-remove"
                      name="trash-o"
                      onClick={() => onItemRemove(item)}
                    />
                  </td>
                </>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <>
      <FileInput
        disabled={isUploading}
        fileSizeLimit={fileSizeLimit}
        filesNumberLimit={filesNumberLimit}
        onFilesAdded={(filesAdded) => {
          onFilesAdded(filesAdded);
        }}
        {...props}
      />

      {files.length > 0 && renderFilesTable()}

      {files.length > filesNumberLimit && (
        <div>
          <Icon name="exclamation-circle" size="lg" />
          <span>Du kan ladda upp max {filesNumberLimit} filer åt gången.</span>
        </div>
      )}

      <Button
        className="mt-4"
        disabled={
          isUploading || files.length > filesNumberLimit || files.length === 0
        }
        onClick={() => setShowConfirmationModal(true)}
      >
        Ladda upp valda filer
      </Button>

      {showConfirmationModal && (
        <UploadConfirmationModal
          onDismiss={() => setShowConfirmationModal(false)}
          onConfirm={() => {
            onUploadClick(files);
            setShowConfirmationModal(false);
          }}
        >
          {renderFilesTable(true)}
        </UploadConfirmationModal>
      )}
    </>
  );
}
